
import { getFileNameFromPath } from 'app/_utils/file-path.utils';

export enum CompetencyStatus {
  Approved = 'APPROVED',
  Rejected = 'DOCUMENT_REJECTED',
  Expired = 'EXPIRED',
  Unverified = 'UNVERIFIED',
  DocumentRequested = 'DOCUMENT_REQUESTED',
  DocumentSubmitted = 'DOCUMENT_SUBMITTED',
}

export function isPendingLikeStatus(status: CompetencyStatus): boolean {
  return status === CompetencyStatus.Unverified
    || status === CompetencyStatus.DocumentRequested
    || status === CompetencyStatus.DocumentSubmitted;
}

export function shouldShowWorkerVisibleCompetency(competency: UserCompetency): boolean {
  if (!competency.hasCompetencyApproval) {
    return false;
  }

  return competency.status === CompetencyStatus.Approved
    || competency.status === CompetencyStatus.Rejected
    || competency.status === CompetencyStatus.Expired
    || competency.status === CompetencyStatus.DocumentRequested
    || competency.status === CompetencyStatus.DocumentSubmitted;
}

export interface CompetencyBase {
  id: number;
  type: string;
  name: string;
  oem?: string;
  model?: string;
  rating?: string;
}

export const COMPETENCY_CATEGORY_LABELS: Record<string, string> = {
  TURBINE: 'Turbine',
  GENERAL_SITE: 'Site',
  SITE: 'Site',
};

export class UserCompetency implements CompetencyBase {
  id: number;
  competencyTypeId?: number;
  type: string;
  competencyCategory: string;
  name: string;
  shortName?: string;
  oem?: string;
  model?: string;
  rating?: string;
  status: CompetencyStatus;
  expiryDate?: string | Date;
  documentFileName?: string;
  reason?: string;
  rejectedBy?: string;
  hasCompetencyApproval: boolean;

  public deserialize(input: any): this {
    this.id = input.competencyId;
    this.competencyTypeId = input.competencyTypeId || undefined;
    this.type = COMPETENCY_CATEGORY_LABELS[input.competencyType] || input.competencyType;
    this.competencyCategory = input.competencyType;
    this.name = input.competencyName;
    this.shortName = input.competencyShortName || undefined;
    this.oem = input.manufacturer || undefined;
    this.model = input.models && input.models.length ? input.models.join(', ') : undefined;
    this.rating = input.ratings && input.ratings.length ? input.ratings.join(', ') : undefined;

    this.hasCompetencyApproval = !!(input.competencyApprovals && input.competencyApprovals.length > 0);
    const approval = (input.competencyApprovals && input.competencyApprovals[0]) || null;
    const rawStatus = approval ? approval.status : null;
    this.status = this.mapApprovalStatus(rawStatus);
    this.documentFileName = (approval && approval.supportingDocumentation)
      || input.supportingDocumentation
      || undefined;
    this.documentFileName = this.documentFileName ? getFileNameFromPath(this.documentFileName) : undefined;
    this.expiryDate = approval ? approval.expiryDate : undefined;
    this.reason = approval ? approval.reason : undefined;
    this.rejectedBy = (approval && approval.status === CompetencyStatus.Rejected) ? approval.approvalLastModifiedByName : undefined;
    return this;
  }

  private mapApprovalStatus(rawStatus: string | null): CompetencyStatus {
    switch (rawStatus) {
      case CompetencyStatus.Approved:
        return CompetencyStatus.Approved;
      case CompetencyStatus.Expired:
        return CompetencyStatus.Expired;
      case CompetencyStatus.Rejected:
        return CompetencyStatus.Rejected;
      case CompetencyStatus.DocumentRequested:
        return CompetencyStatus.DocumentRequested;
      case CompetencyStatus.DocumentSubmitted:
        return CompetencyStatus.DocumentSubmitted;
      case CompetencyStatus.Unverified:
      case 'PENDING':
      default:
        return CompetencyStatus.Unverified;
    }
  }

}

export class WorkOrderCompetency implements CompetencyBase {
  id: number;
  competencyTypeId: number;
  type: string;
  competencyCategory: string;
  name: string;
  oem?: string;
  model?: string;
  rating?: string;

  applyToParty: boolean;

  public deserialize(input: any): this {
    this.id = input.id;
    this.competencyTypeId = input.competencyTypeId;
    this.type = COMPETENCY_CATEGORY_LABELS[input.competencyType];
    this.competencyCategory = input.competencyType;
    this.name = input.competencyTypeName;

    this.oem = input.manufacturer;
    this.model = input.model;
    this.rating = input.rating;
    this.applyToParty = input.applyToParty;

    return this;
  }
}

export function haveSameCompetencyDefinition(
  leftCompetency: { type?: string; name?: string; oem?: string; model?: string; rating?: string },
  rightCompetency: { type?: string; name?: string; oem?: string; model?: string; rating?: string },
): boolean {
  return competencyDefinitionKey(leftCompetency) === competencyDefinitionKey(rightCompetency);
}

export interface NewCompetencyForDuplicateCheck {
  competencyTypeId?: number;
  isTurbine?: boolean;
  oem?: string;
  models?: string[];
  ratings?: string[];
}

/**
 * Accepts any of the competency types :
 * - UserCompetency / WorkOrderCompetency (singular `name`, `oem`, `model`, `rating`)
 * - ProcedureOemCompetencyDto-style (`competencyTypeName`, `manufacturer`, `models[]`, `ratings[]`)
 *
 * Made so that various types can be passed in - method handles passing revelvant data
 */
export interface ExistingCompetencyForDuplicateCheck {
  id?: any;
  competencyTypeId?: number;
  status?: string;
  oem?: string;
  model?: string;
  rating?: string;
}

export function findDuplicateCompetency<T extends ExistingCompetencyForDuplicateCheck>(
  newCompetency: NewCompetencyForDuplicateCheck,
  existing: T[],
): T | null {
  const competencyTypeId = Number(newCompetency.competencyTypeId);
  const isTurbine = !!newCompetency.isTurbine;
  const oem = newCompetency.oem || '';
  const models = normalizeArrayValues(newCompetency.models);
  const ratings = normalizeArrayValues(newCompetency.ratings);

  return (existing || []).find((candidate) =>
    hasSameCompetencyDefinition(candidate, competencyTypeId, isTurbine, oem, models, ratings)
  ) || null;
}

function splitValues(value: string | undefined): string[] {
  return (value || '')
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
}

function hasSameCompetencyDefinition(
  competency: ExistingCompetencyForDuplicateCheck,
  competencyTypeId: number,
  isTurbine: boolean,
  oem: string,
  models: string[],
  ratings: string[],
): boolean {
  if (!competencyTypeId || Number(competency.competencyTypeId) !== competencyTypeId) {
    return false;
  }

  if (!isTurbine) {
    return true;
  }

  return oem === (competency.oem || '')
    && haveSameNormalizedStringArray(
      models,
      normalizeArrayValues(splitValues(competency.model)),
    )
    && haveSameNormalizedStringArray(
      ratings,
      normalizeArrayValues(splitValues(competency.rating)),
    );
}

function normalizeArrayValues(values: string[] | undefined): string[] {
  return Array.from(new Set((values || []).filter((value) => !!value))).sort();
}

function haveSameNormalizedStringArray(left: string[], right: string[]): boolean {
  return left.length === right.length
    && left.every((value, index) => value === right[index]);
}

function competencyDefinitionKey(competency: { type?: string; name?: string; oem?: string; model?: string; rating?: string }): string {
  return [
    normalizeCompetencyDefinitionValue(competency.type),
    normalizeCompetencyDefinitionValue(competency.name),
    normalizeCompetencyDefinitionValue(competency.oem),
    normalizeCompetencyDefinitionValue(competency.model),
    normalizeCompetencyDefinitionValue(competency.rating),
  ].join('|');
}

function normalizeCompetencyDefinitionValue(value: string | undefined): string {
  return (value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

export class WorkOrderCompetencyGroup {
  workOrderId: number;
  workOrderDescription: string;
  requiredCompetencies: WorkOrderCompetency[];

  public deserialize(input: any): this {
    this.workOrderId = input.workOrderId;
    this.workOrderDescription = input.workOrderDescription;
    this.requiredCompetencies = (input.requiredCompetencies || [])
      .filter((c) => !c.deleted)
      .map((c) => new WorkOrderCompetency().deserialize(c));
    return this;
  }
}

export type Competency = UserCompetency | WorkOrderCompetency;

export interface CompetencyStatusCount {
  key: string;
  count: number;
}

export interface UserCompetencyStatusCounts {
  userId: string;
  total: number;
  statuses: CompetencyStatusCount[];
}

export interface CategorizedCompetencies {
  turbine: UserCompetency[];
  site: UserCompetency[];
}

export interface WorkerSiteCompetencyCounts {
  skylarkUserId: number;
  approvedCount: number;
  expiredCount: number;
  rejectedCount: number;
  pendingCount: number;
}
